'use client';

import React, { useEffect, useState } from 'react';

/**
 * A fixed readout of where you are in the page, shown only at 1440px and above
 * where the 1280px container leaves real margin to put it in.
 *
 * The reference site solves the same problem with a ruler strip along the
 * bottom edge — orientation in a page far too long to hold in your head. A
 * ruler suits a technical drawing; a numbered channel list suits a diagnostic
 * report, which is what this page is. Same job, different vernacular.
 *
 * It is `pointer-events: none` and `aria-hidden`: the numbering repeats what
 * the section headings already say, so for a screen reader it is noise. Anyone
 * navigating by keyboard uses the headings.
 */
const SECTIONS = [
  { id: 'hero', label: 'Signals' },
  { id: 'terms', label: 'Terms' },
  { id: 'challenges', label: 'Challenges' },
  { id: 'services', label: 'Services' },
  { id: 'work', label: 'Work' },
  { id: 'how-it-works', label: 'Process' },
  { id: 'assessment', label: 'Assessment' },
  { id: 'about', label: 'About' },
  { id: 'insights', label: 'Insights' },
  { id: 'contact', label: 'Contact' },
];

export default function SectionRail() {
  const [active, setActive] = useState('hero');
  const [onDark, setOnDark] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(max-width: 1439px)').matches) return;

    const nodes = SECTIONS.map((s) => document.getElementById(s.id)).filter((n): n is HTMLElement =>
      Boolean(n)
    );
    if (!nodes.length) return;

    // The band across the middle of the viewport is what counts as "here".
    // Using the whole viewport makes two sections active at once on a tall
    // screen, and the rail flickers between them.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    nodes.forEach((n) => observer.observe(n));

    /* The rail is fixed and the page changes ground colour four times beneath
       it, so "is what I am sitting on dark?" cannot be answered from the
       theme alone — the dark chapter is dark in light mode, and the insights
       masthead inverts. Read the colour actually painted behind the rail and
       decide from its luminance.

       elementFromPoint needs the rail out of its own way, hence the
       pointer-events:none in the stylesheet: the topmost element at that
       coordinate is the section, not the rail. */
    const probe = () => {
      const railX = 34;
      const railY = window.innerHeight / 2;
      let el = document.elementFromPoint(railX, railY) as HTMLElement | null;
      while (el) {
        const bg = getComputedStyle(el).backgroundColor;
        const m = bg.match(/[\d.]+/g);
        if (m && m.length >= 3 && (m.length < 4 || Number(m[3]) > 0.5)) {
          const [r, g, b] = m.map(Number);
          // Rec. 601 luma is enough to answer light-or-dark and is a third of
          // the arithmetic of a full relative-luminance conversion.
          setOnDark((0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5);
          return;
        }
        el = el.parentElement;
      }
    };
    probe();
    window.addEventListener('scroll', probe, { passive: true });
    window.addEventListener('resize', probe);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', probe);
      window.removeEventListener('resize', probe);
    };
  }, []);

  return (
    /* data-no-dots: the one piece of furniture that lives in the page's
       margin, which is where the dot field lives too. Atmosphere reads this
       and keeps the field off it. */
    <div className="m-rail" data-no-dots data-on-dark={onDark} aria-hidden="true">
      {SECTIONS.map((s, i) => (
        <span key={s.id} className="m-rail__item" data-active={active === s.id}>
          <span className="m-rail__tick" />
          {String(i + 1).padStart(2, '0')}
        </span>
      ))}
    </div>
  );
}
